import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { BuildingType } from '../../../types/BuildingTypeEnums';
import { BuildingSummary } from '../../../types/protocols';
import { ServiceType, TimeGrouping } from '../types/analysis.types';
import { todayStockholm } from '../../../Helpers/leaseUtils';
import { BuildingServicesData, useYearComparison } from '../../../hooks/analysis/useYearComparison';
import {
    comparisonYearChoices,
    ComparisonMetric,
    defaultComparisonYears,
    parseYearsParam,
    VISITS_METRIC,
    yearsWithLogs,
} from '../utils/yearComparison';
import { MONTH_NAMES } from '../utils/constants';
import YearComparisonTable from './YearComparisonTable';
import YearComparisonChart from './YearComparisonChart';
import TimeGroupingControl from './TimeGroupingControl';
import MetricPicker from './MetricPicker';

/** URL param holding the compared years ('2025,2026') */
export const YEARS_PARAM = 'ar';

interface YearComparisonViewProps {
    /** The shared filters, shown at the top of the main card, with the År picker added to their row */
    renderControls: (yearPicker: React.ReactNode) => React.ReactNode;
    isLoading: boolean;
    servicesData: BuildingServicesData | null;
    buildings: BuildingSummary[];
    buildingTypes: BuildingType[];
    userGroup: string | null;
    /** Picked buildings; empty = all */
    buildingKeys: string[];
    /** Shown instead of the chart and the table, e.g. when no picked building matches the filters */
    emptyMessage?: string | null;
    serviceTypes: ServiceType[];
    timeGrouping: TimeGrouping;
    onTimeGroupingChange: (grouping: TimeGrouping) => void;
}

/** '28 okt' */
const formatDayMonth = (date: string): string =>
    `${Number(date.slice(8, 10))} ${MONTH_NAMES[Number(date.slice(5, 7)) - 1].toLowerCase()}`;

/**
 * The "Jämför år" tab: the main card (filters, years, chart) and the totals
 * table in a card of its own, beside it when there is room, else below.
 */
const YearComparisonView: React.FC<YearComparisonViewProps> = ({
    renderControls,
    isLoading,
    servicesData,
    buildings,
    buildingTypes,
    userGroup,
    buildingKeys,
    emptyMessage,
    serviceTypes,
    timeGrouping,
    onTimeGroupingChange,
}) => {
    const [params, setParams] = useSearchParams();
    const [sameDate, setSameDate] = useState(true);
    const [metric, setMetric] = useState<ComparisonMetric>(VISITS_METRIC);

    const currentYear = useMemo(() => Number(todayStockholm().slice(0, 4)), []);
    // This year, and up to five years back that have logs
    const availableYears = useMemo(
        () => comparisonYearChoices(currentYear, yearsWithLogs(servicesData?.servicesLogs)),
        [currentYear, servicesData],
    );
    const yearsParam = params.get(YEARS_PARAM);
    const years = useMemo(() => {
        const picked = parseYearsParam(yearsParam, availableYears);
        return yearsParam === null ? defaultComparisonYears(availableYears, currentYear) : picked;
    }, [yearsParam, availableYears, currentYear]);

    const comparison = useYearComparison({
        servicesData,
        buildings,
        buildingTypes,
        userGroup,
        buildingKeys,
        serviceTypes,
        years,
        sameDate,
        grouping: timeGrouping,
        metric,
    });

    const toggleYear = (year: number, checked: boolean) => {
        const next = (checked ? [...years, year] : years.filter(y => y !== year)).sort((a, b) => a - b);
        setParams(prev => {
            const updated = new URLSearchParams(prev);
            updated.set(YEARS_PARAM, next.join(','));
            return updated;
        }, { replace: true });
    };

    const metricOptions = [
        { value: VISITS_METRIC, label: 'Besök' },
        ...serviceTypes
            .filter((type): type is ServiceType & { key: string } => Boolean(type.key))
            .map(type => ({ value: type.key, label: type.title || type.key })),
    ];
    const selectedType = serviceTypes.find(type => type.key === metric);
    const metricLabel = metricOptions.find(option => option.value === metric)?.label ?? 'Besök';

    const yearPicker = availableYears.length >= 2 && (
        <div className="control-group year-comparison-years" role="group" aria-labelledby="comparison-years-label">
            <span id="comparison-years-label" className="control-label">År</span>
            <div className="year-comparison-year-options">
                {[...availableYears].reverse().map(year => (
                    <Form.Check
                        key={year}
                        inline
                        type="checkbox"
                        id={`comparison-year-${year}`}
                        label={String(year)}
                        checked={years.includes(year)}
                        onChange={event => toggleYear(year, event.target.checked)}
                    />
                ))}
            </div>
        </div>
    );

    const mainCard = (content: React.ReactNode) => (
        <div className="analysis-card analysis-main-card year-comparison-main std-box dropshadow">
            {renderControls(yearPicker)}
            {content}
        </div>
    );

    if (isLoading && !servicesData) {
        return mainCard(<div className="loading-container"><p>Laddar data...</p></div>);
    }

    if (availableYears.length < 2) {
        return mainCard(
            <div className="empty-container">
                <p>Det finns ingen data för de senaste fem åren att jämföra med.</p>
            </div>,
        );
    }

    const ready = years.length > 0 && !emptyMessage;

    return (
        <div className="year-comparison-layout">
            {mainCard(
                <>
                    {emptyMessage ? (
                        <div className="empty-container"><p>{emptyMessage}</p></div>
                    ) : years.length === 0 ? (
                        <div className="empty-container"><p>Välj de år du vill jämföra.</p></div>
                    ) : (
                        <>
                            <div className="year-comparison-chart-controls">
                                <TimeGroupingControl value={timeGrouping} onChange={onTimeGroupingChange} />
                                <MetricPicker choices={metricOptions} value={metric} onChange={setMetric} />
                            </div>
                            <YearComparisonChart
                                rows={comparison.chartRows}
                                years={years}
                                metricLabel={metricLabel}
                                grouping={timeGrouping}
                                unit={selectedType?.unit}
                            />
                        </>
                    )}
                </>,
            )}

            {ready && (
                <div className="analysis-card year-comparison-table-card std-box dropshadow">
                    <h2 className="summary-title">Totalt per år</h2>
                    {comparison.cutOffDate && (
                        <Form.Check
                            type="checkbox"
                            id="comparison-same-date"
                            className="year-comparison-same-date"
                            checked={sameDate}
                            onChange={event => setSameDate(event.target.checked)}
                            label={`Jämför till samma datum (t.o.m. ${formatDayMonth(comparison.cutOffDate)})`}
                        />
                    )}
                    <p className="year-comparison-hint">Klicka på en rad för att visa den i diagrammet.</p>
                    <YearComparisonTable
                        columns={comparison.columns}
                        serviceTypes={serviceTypes}
                        chartedMetric={metric}
                        onChartMetric={setMetric}
                    />
                    <p className="year-comparison-hint">
                        Företag avser byggnadens nuvarande ansvariga företag.
                    </p>
                </div>
            )}
        </div>
    );
};

export default YearComparisonView;
